import { ErrorMessage } from '@components/Form'
import {
  CANDIDATE_ZONE_COUNTS,
  DEFAULT_BREAK_METHOD,
  DEFAULT_SMALLEST_ZONE,
  EDGE_ROUNDINGS,
  EdgeRounding,
  SMALLEST_ZONES,
  ZONE_SIZES,
  type ZoneLabel,
  ZoneLabelKind,
  type ZoneLayerFailure,
  ZoneLayerKind,
  ZoneSelectionKind,
  type ZoneSource,
  ZoneSourceFailure,
  ZoneSourceKind,
  useZoneTuning
} from '@features/fields/api/useZoneTuning'
import {
  readyZoning,
  useZoneTuningAuroraTools,
  withoutCustomBreaks,
  type ZoneTuningSettings
} from '@features/fields/hooks/useZoneTuningAuroraTools'
import { AuroraControl } from '@features/misc/auroraControls/AuroraControl'
import { AwaitKind } from '@features/misc/auroraControls/auroraControlsStore'
import { AuroraControlId, type PrescriptionProduct, ZoningStatus } from 'api-validations/aurora'
import { useEffect, useState } from 'react'
import { ButtonKind, CircleSpinner, PanoramaButton, PanoramaSelectMenu } from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { createVariants } from 'shared-ui/lib'
import { type LocalizationContext, useLocalization } from 'shared-ui/localization'
import {
  createZone,
  resetZoneEditing,
  type SoilSurveyLegendEntry,
  useCurrentZones,
  useMapContext,
  useSoilSurveyEntryLabel
} from 'shared-ui/map'
import { BreakMethod, isNil, ZoneBreaksFailure } from 'utils'

import type { Zone } from './PrescriptionDetailsTab'
import { ZoneBreakSlider } from './ZoneBreakSlider'
import { ZoneCountSelect } from './ZoneCountSelect'
import { useZoneSourceName, ZoneSourcePicker } from './ZoneSourcePicker'

const BREAK_METHODS = [BreakMethod.NaturalBreaks, BreakMethod.EqualCount, BreakMethod.EqualInterval]

const METHOD_LABEL_KEYS: Record<BreakMethod, string> = {
  [BreakMethod.NaturalBreaks]: 'planning.zoneTuning.methods.naturalBreaks',
  [BreakMethod.EqualCount]: 'planning.zoneTuning.methods.equalCount',
  [BreakMethod.EqualInterval]: 'planning.zoneTuning.methods.equalInterval'
}

const EDGE_LABEL_KEYS: Record<EdgeRounding, string> = {
  [EdgeRounding.Straight]: 'planning.zoneTuning.edgeRoundings.straight',
  [EdgeRounding.Rounded]: 'planning.zoneTuning.edgeRoundings.rounded',
  [EdgeRounding.VeryRounded]: 'planning.zoneTuning.edgeRoundings.veryRounded'
}

const FAILURE_MESSAGE_KEYS: Record<ZoneLayerFailure, string> = {
  [ZoneBreaksFailure.NoData]: 'planning.zoneTuning.errors.noData',
  [ZoneBreaksFailure.SingleValue]: 'planning.zoneTuning.errors.singleValue',
  [ZoneSourceFailure.Uninterpolable]: 'planning.zoneTuning.errors.uninterpolable'
}

const updatingClasses = createVariants({
  base: 'flex flex-col gap-2 transition-opacity',
  variants: {
    updating: {
      true: 'opacity-50',
      false: 'opacity-100'
    }
  }
})

const zoneName = ({
  label,
  unit,
  formatMetric,
  entryLabel
}: {
  label: ZoneLabel
  unit: string | undefined
  formatMetric: LocalizationContext['formatMetric']
  entryLabel: (entry: SoilSurveyLegendEntry) => string
}) => {
  if (label.kind === ZoneLabelKind.Class) return entryLabel(label.entry)
  const formatBound = (value: number) => formatMetric({ value, unit }, { includeUnits: false })
  return `${formatBound(label.min)} - ${formatBound(label.max)}`
}

const DEFAULT_SETTINGS: ZoneTuningSettings = {
  method: DEFAULT_BREAK_METHOD,
  selection: { kind: ZoneSelectionKind.Recommended },
  smallestZone: DEFAULT_SMALLEST_ZONE,
  edges: EdgeRounding.Rounded
}

export const ZoneTuning = ({
  fieldId,
  boundaryId,
  initialSource,
  onAccept,
  onCancel
}: {
  fieldId: string
  boundaryId: string
  initialSource: ZoneSource | undefined
  onAccept: (zones: Zone[], products: PrescriptionProduct[]) => void
  onCancel: () => void
}) => {
  const { t } = useLocalization()
  const { map } = useMapContext()
  const [source, setSource] = useState<ZoneSource | undefined>(initialSource)
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const sourceName = useZoneSourceName({ fieldId, source })
  const sourceValue =
    sourceName === undefined
      ? t(($) => $.auroraControls.values.noLayer)
      : t(($) => $.auroraControls.values.source, { source: sourceName })

  return (
    <div className="flex flex-col gap-4">
      <AuroraControl
        id={AuroraControlId.ZoneSource}
        value={sourceValue}
        awaits={{ kind: AwaitKind.Answer, satisfied: sourceName !== undefined }}
      >
        <ZoneSourcePicker
          fieldId={fieldId}
          source={source}
          onSourceChange={(next) => {
            setSource(next)
            setSettings((current) => ({
              ...current,
              selection: { kind: ZoneSelectionKind.Recommended }
            }))
            if (isNil(next)) {
              resetZoneEditing()
              map.drawTool.clear()
            }
          }}
        />
      </AuroraControl>

      {isNil(source) ? (
        <TuningButtons onCancel={onCancel} />
      ) : (
        <SourceTuning
          fieldId={fieldId}
          boundaryId={boundaryId}
          source={source}
          sourceName={sourceName}
          settings={settings}
          onSettingsChange={(change) => setSettings((current) => ({ ...current, ...change }))}
          onAccept={onAccept}
          onCancel={onCancel}
        />
      )}
    </div>
  )
}

const SourceTuning = ({
  fieldId,
  boundaryId,
  source,
  sourceName,
  settings,
  onSettingsChange,
  onAccept,
  onCancel
}: {
  fieldId: string
  boundaryId: string
  source: ZoneSource
  sourceName: string | undefined
  settings: ZoneTuningSettings
  onSettingsChange: (change: Partial<ZoneTuningSettings>) => void
  onAccept: (zones: Zone[], products: PrescriptionProduct[]) => void
  onCancel: () => void
}) => {
  const { t, tByPath, formatMetric } = useLocalization()
  const { map } = useMapContext()
  const zoneEditing = useCurrentZones()
  const entryLabel = useSoilSurveyEntryLabel()
  const { method, selection, smallestZone, edges } = settings

  const tuning = useZoneTuning({
    fieldId,
    boundaryId,
    source,
    method,
    selection,
    smallestZone,
    edges
  })
  const placed = tuning.preview.data

  useEffect(() => {
    if (isNil(placed)) return

    resetZoneEditing()
    placed.zones.forEach(({ key, label }) => {
      if (key === undefined) return
      createZone({
        id: key,
        name: zoneName({ label, unit: placed.unit, formatMetric, entryLabel })
      })
    })

    map.drawTool.clear()
    placed.featureCollection.features.forEach((feature) => {
      map.drawTool.addFeatures(feature)
    })
  }, [placed, map, formatMetric, entryLabel])

  const formatShare = (share: number) => formatMetric({ value: share * 100, unit: '%' })
  const formatValue = (value: number) =>
    formatMetric({ value, unit: tuning.unit }, { includeUnits: false })
  const smoothsEdges = source.kind === ZoneSourceKind.AsApplied
  const isClasses = tuning.layerKind === ZoneLayerKind.Classes

  const methodOptions = BREAK_METHODS.map((id) => ({
    id,
    name: tByPath(METHOD_LABEL_KEYS[id], { defaultValue: id })
  }))

  const smallestZoneOptions = SMALLEST_ZONES.map((id) => ({
    id,
    name: formatMetric({ value: ZONE_SIZES[id].minZoneSize, unit: 'ac' })
  }))

  const edgeOptions = EDGE_ROUNDINGS.map((id) => ({
    id,
    name: tByPath(EDGE_LABEL_KEYS[id], { defaultValue: id })
  }))

  const scoreText = (score: { varianceExplained: number; reassignedShare: number }) =>
    smoothsEdges
      ? t(($) => $.planning.zoneTuning.score, {
          fit: formatShare(score.varianceExplained),
          smoothed: formatShare(score.reassignedShare)
        })
      : t(($) => $.planning.zoneTuning.scoreFit, { fit: formatShare(score.varianceExplained) })

  const scoreList = tuning.scores.data ?? []
  const scoresReady = tuning.scores.isSuccess && !tuning.scores.isPlaceholderData
  const scoresSettled = scoresReady || tuning.scores.isError
  const scoring = tuning.scores.isFetching

  const zoneCountDetail = ({
    score,
    unavailable
  }: {
    score: { varianceExplained: number; reassignedShare: number } | undefined
    unavailable: boolean
  }) => {
    if (score !== undefined) return scoreText(score)
    if (unavailable) return t(($) => $.planning.zoneTuning.unavailable)
    return t(($) => $.planning.zoneTuning.scoring)
  }

  const zoneCountOptions = CANDIDATE_ZONE_COUNTS.map((count) => {
    const score = scoresReady ? scoreList.find((option) => option.zoneCount === count) : undefined
    const unavailable = scoresSettled && score === undefined
    return {
      zoneCount: count,
      name: t(($) => $.planning.zoneTuning.zones, { count }),
      detail: zoneCountDetail({ score, unavailable }),
      recommended: tuning.recommendedZoneCount === count,
      disabled: unavailable
    }
  })

  const customScore = isNil(placed?.score) ? '' : scoreText(placed.score)
  const customZoning =
    selection.kind === ZoneSelectionKind.Custom
      ? { name: t(($) => $.planning.zoneTuning.customBreaks), detail: customScore }
      : undefined

  const noCountAvailable = scoresReady && scoreList.length === 0
  const noCountHoldsTogether =
    scoresReady && scoreList.length > 0 && tuning.recommendedZoneCount === undefined

  const layerFailure = tuning.layer.data?.ok === false ? tuning.layer.data.reason : undefined
  const layerMessage =
    layerFailure === undefined
      ? undefined
      : tByPath(FAILURE_MESSAGE_KEYS[layerFailure], { defaultValue: layerFailure })
  const requestFailed = tuning.layer.isError || tuning.scores.isError || tuning.preview.isError
  const showsZoneCount =
    layerFailure === undefined && !tuning.layer.isLoading && !tuning.layer.isError && !isClasses

  const rowLabel = ({ label, unit }: { label: ZoneLabel; unit: string | undefined }) =>
    label.kind === ZoneLabelKind.Range
      ? `${formatMetric({ value: label.min, unit }, { includeUnits: false })} – ${formatMetric({ value: label.max, unit })}`
      : entryLabel(label.entry)

  const colorOf = (key: string | undefined) => {
    const zone = key === undefined ? undefined : zoneEditing?.zones.get(key)
    return zone?.color ?? 'transparent'
  }

  const breaks = tuning.selectedZones?.slice(1).map((zone) => zone.min) ?? []
  const zoneRows = (placed?.zones ?? []).map(({ key, label, acres }, index) => ({
    index,
    color: colorOf(key),
    label: rowLabel({ label, unit: placed?.unit }),
    acres: formatMetric({ value: acres, unit: 'ac' })
  }))
  const zoneColors = zoneRows.map(({ color }) => color)

  const acceptDisabled =
    !tuning.preview.isSuccess || tuning.preview.isFetching || tuning.preview.isPlaceholderData

  const acceptZones = (products: PrescriptionProduct[]) => {
    if (isNil(zoneEditing)) return
    onAccept([...zoneEditing.zones.values()], products)
  }

  const zoningSettled =
    (isClasses ||
      (tuning.scores.isSuccess && !tuning.scores.isPlaceholderData && !tuning.scores.isFetching)) &&
    !tuning.preview.isFetching &&
    !tuning.preview.isPlaceholderData

  const zoningFailure = (): string | undefined => {
    if (layerMessage !== undefined) return layerMessage
    if (requestFailed) return t(($) => $.messages.errorOccurred)
    if (noCountAvailable) return t(($) => $.planning.zoneTuning.errors.singleValue)
    if (noCountHoldsTogether && placed === undefined) {
      return t(($) => $.planning.zoneTuning.errors.noisy)
    }
    return undefined
  }

  const describeZoning = () => {
    if (zoningSettled && placed !== undefined && sourceName !== undefined) {
      return readyZoning({
        settings,
        smoothsEdges,
        source: sourceName,
        zoneCount: placed.zones.length,
        recommendedZoneCount: tuning.recommendedZoneCount ?? null,
        candidates: scoreList.map((score) => ({
          zoneCount: score.zoneCount,
          fit: score.varianceExplained,
          smoothed: smoothsEdges ? score.reassignedShare : null
        })),
        unit: placed.unit ?? null,
        zones: placed.zones.map(({ label, acres }) => ({
          name: zoneName({ label, unit: placed.unit, formatMetric, entryLabel }),
          acres
        }))
      })
    }
    const reason = zoningFailure()
    if (reason !== undefined) return { status: ZoningStatus.Failed as const, reason }
    return undefined
  }
  const zoning = describeZoning()

  useZoneTuningAuroraTools({
    source,
    settings,
    zoning,
    onSettingsChange,
    acceptZones
  })

  const describeZoneBreaks = () => {
    if (zoning?.status !== ZoningStatus.Ready) return t(($) => $.auroraControls.values.zonesLoading)
    const ranges = zoning.zones
      .map(({ name, acres }) =>
        t(($) => $.auroraControls.values.zone, {
          name,
          acres: formatMetric({ value: acres, unit: 'ac' })
        })
      )
      .join(', ')
    return t(($) => $.auroraControls.values.zones, {
      zones: t(($) => $.planning.zoneTuning.zones, { count: zoning.zoneCount }),
      ranges
    })
  }
  const zoneBreaksValue = describeZoneBreaks()

  return (
    <>
      {tuning.layer.isLoading && (
        <div className="pan-detail flex items-center gap-2 text-gray-600">
          <CircleSpinner size="size-4" />
          {t(($) => $.planning.zoneTuning.readingLayer)}
        </div>
      )}
      {layerMessage !== undefined && <ErrorMessage>{layerMessage}</ErrorMessage>}

      {showsZoneCount && (
        <div className={updatingClasses({ updating: scoring })}>
          <ZoneCountSelect
            label={
              <span className="flex items-center gap-2">
                {t(($) => $.planning.zoneTuning.zoneCount)}
                {scoring && <CircleSpinner size="size-4" />}
              </span>
            }
            recommendedLabel={t(($) => $.planning.zoneTuning.bestFit)}
            options={zoneCountOptions}
            value={tuning.selectedZoneCount}
            custom={customZoning}
            onChange={(zoneCount) =>
              onSettingsChange({ selection: { kind: ZoneSelectionKind.Count, zoneCount } })
            }
          />
          {noCountAvailable && (
            <p className="pan-detail text-red-600">
              {t(($) => $.planning.zoneTuning.errors.singleValue)}
            </p>
          )}
          {noCountHoldsTogether && (
            <p className="pan-detail text-red-600">
              {t(($) => $.planning.zoneTuning.errors.noisy)}
            </p>
          )}
        </div>
      )}

      {smoothsEdges && (
        <div className="flex gap-3">
          <PanoramaSelectMenu
            label={t(($) => $.planning.zoneTuning.smallestZone)}
            size="fill"
            value={smallestZoneOptions.find((option) => option.id === smallestZone)}
            options={smallestZoneOptions}
            onChange={({ id }) => onSettingsChange({ smallestZone: id })}
          />

          <PanoramaSelectMenu
            label={t(($) => $.planning.zoneTuning.edges)}
            size="fill"
            value={edgeOptions.find((option) => option.id === edges)}
            options={edgeOptions}
            onChange={({ id }) => onSettingsChange({ edges: id })}
          />
        </div>
      )}

      {!isClasses && (
        <PanoramaSelectMenu
          label={t(($) => $.planning.zoneTuning.method)}
          size="fill"
          value={methodOptions.find((option) => option.id === method)}
          options={methodOptions}
          onChange={({ id }) =>
            onSettingsChange({ method: id, selection: withoutCustomBreaks(selection) })
          }
        />
      )}

      {isClasses && (
        <div className={updatingClasses({ updating: tuning.preview.isFetching })}>
          <span className="pan-label flex items-center gap-2">
            {t(($) => $.labels.zone, { count: zoneRows.length })}
            {tuning.preview.isFetching && <CircleSpinner size="size-4" />}
          </span>
          <ZoneRows rows={zoneRows} />
        </div>
      )}

      {tuning.selectedZones !== undefined && (
        <AuroraControl id={AuroraControlId.ZoneBreaks} value={zoneBreaksValue}>
          <div className={updatingClasses({ updating: tuning.preview.isFetching })}>
            <div className="pan-label flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                {t(($) => $.planning.zoneTuning.breaks)}
                {tuning.preview.isFetching && <CircleSpinner size="size-4" />}
              </span>
            </div>
            {tuning.histogram !== undefined && (
              <ZoneBreakSlider
                histogram={tuning.histogram}
                breaks={breaks}
                colors={zoneColors}
                formatValue={formatValue}
                onCommit={(committed) =>
                  onSettingsChange({
                    selection: { kind: ZoneSelectionKind.Custom, breaks: committed }
                  })
                }
              />
            )}
            <ZoneRows rows={zoneRows} />
          </div>
        </AuroraControl>
      )}

      {requestFailed && <ErrorMessage>{t(($) => $.messages.errorOccurred)}</ErrorMessage>}

      <TuningButtons
        onCancel={onCancel}
        accept={{ onAccept: () => acceptZones([]), disabled: acceptDisabled }}
      />
    </>
  )
}

const TuningButtons = ({
  onCancel,
  accept
}: {
  onCancel: () => void
  accept?: { onAccept: () => void; disabled: boolean }
}) => {
  const { t } = useLocalization()
  const acceptReason =
    accept === undefined ? t(($) => $.planning.zoneTuning.chooseLayer) : t(($) => $.loading)

  return (
    <div className="flex w-full gap-3 border-t border-gray-300 py-5">
      <PanoramaButton
        type="button"
        kind={ButtonKind.Secondary}
        icon={SpriteMap.CloseCircleIcon}
        onClick={onCancel}
        className="w-full"
      >
        {t(($) => $.buttons.cancel)}
      </PanoramaButton>
      <PanoramaButton
        kind={ButtonKind.Primary}
        icon={SpriteMap.CheckCircleIcon}
        onClick={accept?.onAccept}
        disabledWhen={{
          when: accept === undefined || accept.disabled,
          reason: acceptReason
        }}
        className="w-full"
      >
        {t(($) => $.planning.zoneTuning.useZones)}
      </PanoramaButton>
    </div>
  )
}

const ZoneRows = ({
  rows
}: {
  rows: { index: number; color: string; label: string; acres: string }[]
}) =>
  rows.map((row) => (
    <div key={row.index} className="pan-detail flex justify-between text-gray-600">
      <span className="flex items-center gap-2">
        <span className="size-3 rounded-sm" style={{ background: row.color }} />
        {row.label}
      </span>
      <span>{row.acres}</span>
    </div>
  ))
